import React, { useState, useEffect } from 'react'
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuProps,
  Portal,
  Show,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import CustomMenuList from '../CustomMenuList/CustomMenuList'

export interface IDropdownItem {
  id: string
  label: string
  action: () => void
}

export interface DropdownProps {
  onChange: (item: any) => void
  shouldUsePortal?: boolean
  items: IDropdownItem[]
  selected?: IDropdownItem
  buttonProps?: MenuButtonProps
  containerRef?: React.MutableRefObject<HTMLElement | null>
}

function Dropdown({
  onChange,
  shouldUsePortal = true,
  selected,
  buttonProps,
  containerRef,
  items,
  ...rest
}: DropdownProps & Omit<MenuProps, 'children'>) {
  const [optionSelected, setOptionSelected] = useState<IDropdownItem>(
    selected || items[0],
  )
  
  useEffect(() => {
    if (selected) {
      setOptionSelected(selected)   // Sync state when selected changes
    }
  }, [selected])
  
  const handleChange = item => {
    setOptionSelected(item)
    if (onChange) {
      onChange(item)
    }
  }
  return (
    <Menu {...rest}>
      {({ isOpen, onClose }) => (
        <>
          <MenuButton
            mt={'8px'}
            paddingLeft={0}
            fontSize="16px"
            fontWeight="bold"
            data-testid="dropdown"
            as={Button}
            isActive={isOpen}
            rightIcon={
              !isOpen ? (
                <ChevronDownIcon
                  ml={1}
                  fontSize={20}
                />
              ) : (
                <ChevronUpIcon
                  ml={1}
                  fontSize={20}
                />
              )
            }
            _hover={{ color: 'brand.blue' }}
            _active={{ color: 'brand.blue' }}
            bg={{
              base: 'brand.lightGray',
              md: 'transparent',
            }}
            ml={'8px'}
            p={{
              base: '4px 8px',
              md: '0',
            }}
            {...buttonProps}>
            {optionSelected?.label}
          </MenuButton>
          <WrapperPortal
            containerRef={containerRef}
            shouldUsePortal={shouldUsePortal}>
            <Show above="md">
              <CustomMenuList
                items={items}
                onChange={handleChange}
                selected={optionSelected}
              />
            </Show>
            <Show below="md">
              <DropdownMobile
                onClose={onClose}
                onChange={handleChange}
                isOpen={isOpen}
                items={items}
              />
            </Show>
          </WrapperPortal>
        </>
      )}
    </Menu>
  )
}

const WrapperPortal = ({ shouldUsePortal, containerRef, children }) => {
  return shouldUsePortal ? (
    <Portal
      appendToParentPortal
      containerRef={containerRef}>
      {children}
    </Portal>
  ) : (
    <>{children}</>
  )
}

const DropdownMobile = ({ items, onChange, isOpen, onClose, ...rest }) => {
  return (
    <Drawer
      {...rest}
      isOpen={isOpen}
      onClose={onClose}
      placement={'bottom'}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerBody>
          <List p="20px 0">
            {items.map((item, index) => (
              <ListItem
                fontSize="14px"
                fontWeight="bold"
                lineHeight="3em"
                color="brand.textDarkBlue"
                borderRadius="5px"
                _hover={{ backgroundColor: 'brand.lightGray' }}
                _active={{ backgroundColor: 'brand.lightGray' }}
                _focus={{ backgroundColor: 'brand.lightGray' }}
                p="0 12px"
                data-testid="dropdown-item"
                key={item.id || index}
                onClick={() => onChange(item)}
                data-id={item.id}
                // icon={item.icon}
                cursor="pointer">
                {item.label}
              </ListItem>
            ))}
          </List>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

Dropdown.displayName = 'Dropdown'

export default Dropdown
